import { useState, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Typography,
  Alert,
  Box,
} from '@mui/material';
import {
  OpenInNew as OpenInNewIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  RichTextEditorComponent,
  Toolbar,
  HtmlEditor,
  QuickToolbar,
  Inject,
} from '@syncfusion/ej2-react-richtexteditor';

interface QueryLetterPreviewModalProps {
  open: boolean;
  html: string;
  onClose: () => void;
  onDownloadPdf: (editedHtml: string) => Promise<void>;
  isDownloading: boolean;
}

const toolbarSettings = {
  items: [
    'Bold', 'Italic', 'Underline', '|',
    'Formats', 'Alignments', '|',
    'OrderedList', 'UnorderedList', '|',
    'Undo', 'Redo', '|',
    'SourceCode',
  ],
};

const QueryLetterPreviewModal: React.FC<QueryLetterPreviewModalProps> = ({
  open,
  html,
  onClose,
  onDownloadPdf,
  isDownloading,
}) => {
  const rteRef = useRef<RichTextEditorComponent | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const handleDownload = useCallback(async () => {
    setDownloadError(null);
    const editedHtml = rteRef.current?.getHtml() ?? html;
    try {
      await onDownloadPdf(editedHtml);
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : 'Failed to generate PDF');
    }
  }, [html, onDownloadPdf]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { height: '85vh' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" component="span">
          Physician Query Letter Preview
        </Typography>
        <Button size="small" onClick={onClose} startIcon={<CloseIcon />}>
          Close
        </Button>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, overflow: 'hidden' }}>
        {downloadError && (
          <Alert severity="error" onClose={() => setDownloadError(null)} sx={{ m: 1 }}>
            {downloadError}
          </Alert>
        )}
        <Box sx={{ height: '100%', '& .e-richtexteditor': { height: '100% !important', border: 'none' } }}>
          <RichTextEditorComponent
            ref={rteRef}
            value={html}
            toolbarSettings={toolbarSettings}
            height="100%"
          >
            <Inject services={[Toolbar, HtmlEditor, QuickToolbar]} />
          </RichTextEditorComponent>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          You may edit the letter above before viewing.
        </Typography>
        <Button
          variant="contained"
          startIcon={isDownloading ? <CircularProgress size={16} /> : <OpenInNewIcon />}
          onClick={handleDownload}
          disabled={isDownloading}
        >
          {isDownloading ? 'Opening PDF...' : 'View PDF'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QueryLetterPreviewModal;
